import React from 'react';
import Head from '@components/atoms/head/head';
import { defaultMetas } from '@constants/metas/default';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import extractFromContext from '@helpers/utils/extract-from-context';
import { UpdateDataLayerComponent } from '@context/analytics/analytics.context';
import { AlertsProvider } from '@context/alerts.context';
import AlertManagement from '@components/alerts/alerts-manage/alerts-management';
import { usePreferences } from '@context/preferences.context';
import AuthenticationGuard from '@components/atoms/authentication/authentication-guard/authentication-guard';
import DefaultLayout from '@layouts/default-layout';
import useUser from '@hooks/user/use-user';
import { getAppInitialProps } from '@helpers/initial-props';

interface AlertManagementPageProps {
  isLanding: boolean;
}

const AlertManagementPage: NextPageWithLayout<AlertManagementPageProps> = ({ isLanding }) => {
  const { language } = usePreferences();
  const { isAuthenticated } = useUser();
  const pageMetas = defaultMetas[language];
  // TODO: check with data team about this authentication check to update the data layer only when the user is authenticated
  const dataLayer = isAuthenticated
    ? {
        screen_name: '/my_alerts/manage',
        screen_category: 'my_saved_items',
        screen_subcategory: 'my_alerts',
        pageUrl: '/my_alerts/manage',
        env_template: 'my_alerts_manage',
      }
    : {};

  return (
    <>
      <AuthenticationGuard>
        <UpdateDataLayerComponent data={dataLayer} />
        <Head metadata={pageMetas} />
        <AlertsProvider>
          <AlertManagement isLanding={isLanding} />
        </AlertsProvider>
      </AuthenticationGuard>
    </>
  );
};

AlertManagementPage.Layout = [DefaultLayout];
AlertManagementPage.PageType = 'AlertManagementPage';

export const getServerSideProps: GetServerSideProps<AlertManagementPageProps> = async (context) => {
  const { locale } = extractFromContext(context);
  const initialProps = await getAppInitialProps(context);
  const translations = await serverSideTranslations(locale, [
    'default-layout',
    'vc-authentication',
    'a11y',
    'common',
    'saved-searches',
  ]);

  return {
    props: {
      ...initialProps,
      ...translations,
      isLanding: false,
    },
  };
};

export default AlertManagementPage;
